import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, X, User, Lock, Truck, RotateCcw, Sparkles, Gift, Instagram, Twitter } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useEffect, useState } from "react";

// Fill these in with your real profile URLs once your accounts are created.
// Empty strings hide the icon. The footer shows "Coming soon" if all three are empty.
const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/meridienpress.official/",
  pinterest: "",   // e.g. "https://pinterest.com/meridienpress"
  x:         "",   // e.g. "https://x.com/meridienpress"
};

export default function StorefrontLayout({ children }) {
  return (
    <div className="min-h-screen" style={{ background: "var(--paper)" }}>
      <Announcement />
      <Header />
      <main>{children}</main>
      <TrustStrip />
      <Footer />
      <CartDrawer />
      <ExitIntentModal />
    </div>
  );
}

function Announcement() {
  return (
    <div className="trust-strip text-center text-xs font-mono-ui py-2 tracking-widest uppercase">
      LAUNCH WEEK · Use code <span style={{ color: "var(--accent-soft)" }}>FIRST20</span> for 20% off · Instant downloads · Secure Stripe checkout
    </div>
  );
}

function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl rule-b" style={{ background: "rgba(247,244,237,0.85)" }}>
      <div className="max-w-[1320px] mx-auto px-6 sm:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
          <span className="wordmark text-3xl">MeridianPress</span>
          <span className="label-mono mt-1" style={{ color: "var(--accent)" }}>DIGITAL TOOLS · EST 2026</span>
        </Link>

        <form
          onSubmit={(e) => { e.preventDefault(); nav(`/shop?q=${encodeURIComponent(q)}`); }}
          className="hidden md:flex flex-1 max-w-md items-center rounded-full px-2"
          style={{ background: "#ffffff", border: "1px solid var(--slate-200)" }}
        >
          <Search className="w-4 h-4 ml-3" style={{ color: "var(--ink-mute)" }} />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="What do you need help with today?"
            className="px-3 py-2.5 w-full text-sm outline-none bg-transparent placeholder:opacity-60"
          />
        </form>

        <nav className="flex items-center gap-5">
          <Link to="/shop?audience=real-estate" data-testid="nav-real-estate" className="hidden md:inline text-sm font-medium hover:opacity-60 transition">For Real Estate</Link>
          <Link to="/shop?audience=creators" data-testid="nav-creators" className="hidden lg:inline text-sm font-medium hover:opacity-60 transition">For Content Creators</Link>
          <Link to="/shop?audience=saas" data-testid="nav-saas" className="hidden lg:inline text-sm font-medium hover:opacity-60 transition">For SaaS Founders</Link>
          <Link to="/shop?audience=small-business" data-testid="nav-small-business" className="hidden xl:inline text-sm font-medium hover:opacity-60 transition">For Small Business</Link>
          <Link to="/shop" data-testid="nav-shop" className="hidden sm:inline text-sm font-medium hover:opacity-60 transition">All</Link>
          {user && user.role === "customer" ? (
            <Link to="/account" data-testid="nav-account" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium hover:opacity-60 transition">
              <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
            </Link>
          ) : (
            <Link to="/login" data-testid="nav-signin" className="hidden sm:inline text-sm font-medium hover:opacity-60 transition">Sign in</Link>
          )}
          <Link to="/admin/login" data-testid="nav-admin" className="label-mono hidden lg:inline">Studio</Link>
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full transition"
            style={{ background: "var(--ink)", color: "var(--paper)" }}
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="text-sm font-medium">Bag</span>
            <span data-testid="cart-count" className="font-mono-ui text-xs opacity-80">[{count}]</span>
          </button>
        </nav>
      </div>
    </header>
  );
}

function TrustStrip() {
  const items = [
    { icon: <Sparkles className="w-5 h-5" />, label: "Instant downloads" },
    { icon: <Lock className="w-5 h-5" />, label: "Lifetime access" },
    { icon: <Truck className="w-5 h-5" />, label: "Secure Stripe checkout" },
    { icon: <RotateCcw className="w-5 h-5" />, label: "30-day money-back" },
  ];
  return (
    <section className="rule-t mt-24">
      <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((it) => (
          <div key={it.label} className="flex items-center gap-3">
            <span style={{ color: "var(--accent)" }}>{it.icon}</span>
            <span className="text-sm font-medium">{it.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="rule-t" style={{ background: "#ffffff" }}>
      <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-16 grid sm:grid-cols-4 gap-10">
        <div className="sm:col-span-2">
          <div className="wordmark text-4xl">MeridianPress</div>
          <p className="mt-4 text-sm max-w-sm" style={{ color: "var(--ink-soft)" }}>
            Digital tools that help you create, grow & profit. Premium AI prompt packs, Notion templates, planners, guides and toolkits — instantly downloadable.
          </p>
          <div className="mt-4 label-mono">DIGITAL TOOLS · MMXXVI · USA</div>
        </div>
        <div>
          <div className="label-mono mb-3">Shop</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop?category=AI%20Skills%20Guides" className="hover:opacity-60">AI Prompts</Link></li>
            <li><Link to="/shop?category=Templates" className="hover:opacity-60">Templates</Link></li>
            <li><Link to="/shop?category=Digital" className="hover:opacity-60">Digital Downloads</Link></li>
            <li><Link to="/shop" className="hover:opacity-60">All products</Link></li>
          </ul>
        </div>
        <div>
          <div className="label-mono mb-3">Account</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/login" className="hover:opacity-60">Sign in</Link></li>
            <li><Link to="/signup" className="hover:opacity-60">Create account</Link></li>
            <li><Link to="/admin/login" className="hover:opacity-60">Studio →</Link></li>
            <li>
              <a href="mailto:hello@meridianpress.co" className="hover:opacity-60"
                 data-testid="footer-email">hello@meridianpress.co</a>
            </li>
          </ul>
          <div className="label-mono mt-6 mb-2">Follow</div>
          <div className="flex items-center gap-3">
            {SOCIAL_LINKS.instagram && (
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer noopener"
                 aria-label="Instagram" data-testid="social-instagram"
                 className="hover:opacity-60 transition">
                <Instagram className="w-5 h-5" />
              </a>
            )}
            {SOCIAL_LINKS.pinterest && (
              <a href={SOCIAL_LINKS.pinterest} target="_blank" rel="noreferrer noopener"
                 aria-label="Pinterest" data-testid="social-pinterest"
                 className="hover:opacity-60 transition">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12c0 5 3.1 9.4 7.6 11.2-.1-1-.2-2.4 0-3.5.2-.9 1.4-5.7 1.4-5.7s-.4-.7-.4-1.8c0-1.7 1-2.9 2.2-2.9 1 0 1.5.8 1.5 1.7 0 1-.7 2.6-1 4-.3 1.2.6 2.1 1.8 2.1 2.1 0 3.7-2.2 3.7-5.5 0-2.9-2-4.9-4.9-4.9-3.3 0-5.3 2.5-5.3 5.1 0 1 .4 2.1.9 2.7l.1.4c-.1.4-.3 1.2-.4 1.4-.1.2-.2.3-.5.2-1.6-.8-2.6-3.1-2.6-5.1 0-4.1 3-7.9 8.7-7.9 4.6 0 8.1 3.3 8.1 7.6 0 4.5-2.9 8.2-6.8 8.2-1.3 0-2.6-.7-3-1.5l-.8 3.2c-.3 1.2-1.1 2.6-1.7 3.5C9.5 23.7 10.7 24 12 24c6.6 0 12-5.4 12-12S18.6 0 12 0z"/>
                </svg>
              </a>
            )}
            {SOCIAL_LINKS.x && (
              <a href={SOCIAL_LINKS.x} target="_blank" rel="noreferrer noopener"
                 aria-label="X (Twitter)" data-testid="social-x"
                 className="hover:opacity-60 transition">
                <Twitter className="w-5 h-5" />
              </a>
            )}
            {!SOCIAL_LINKS.instagram && !SOCIAL_LINKS.pinterest && !SOCIAL_LINKS.x && (
              <span className="label-mono" style={{ color: "var(--ink-mute)" }}>Coming soon</span>
            )}
          </div>
        </div>
      </div>
      <div className="rule-t">
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="label-mono">© MeridianPress {new Date().getFullYear()}</div>
          <div className="label-mono" style={{ color: "var(--ink-mute)" }}>Secure checkout by Stripe</div>
        </div>
      </div>
    </footer>
  );
}

function CartDrawer() {
  const { open, setOpen, items, remove, setQty, total, count, coupon, setCoupon } = useCart();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const BUNDLE_MIN = 3;
  const BUNDLE_PCT = 25;
  const bundleActive = count >= BUNDLE_MIN;
  const bundleDiscount = bundleActive ? +(total * (BUNDLE_PCT / 100)).toFixed(2) : 0;
  const afterBundle = Math.max(0, total - bundleDiscount);
  const couponDiscount = coupon ? +(afterBundle * (coupon.discount_pct / 100)).toFixed(2) : 0;
  const grandTotal = Math.max(0, +(afterBundle - couponDiscount).toFixed(2));

  const applyCode = async () => {
    setCodeErr("");
    if (!code.trim()) return;
    try {
      const r = await (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/validate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      })).json();
      if (r?.code && r?.discount_pct) {
        setCoupon(r);
        setCode("");
      } else {
        setCodeErr("Invalid code");
      }
    } catch {
      setCodeErr("Invalid code");
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
      <aside data-testid="cart-drawer" className="w-full max-w-md flex flex-col" style={{ background: "var(--paper)" }}>
        <div className="flex items-center justify-between px-6 py-5 rule-b">
          <div className="wordmark text-2xl">Your Bag</div>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && (
            <div className="text-sm" style={{ color: "var(--ink-soft)" }}>
              Your bag is empty. Browse <Link to="/shop" className="underline" onClick={() => setOpen(false)}>the shop</Link>.
            </div>
          )}
          {items.length > 0 && (
            <div
              data-testid="bundle-progress"
              className="p-3 rounded-lg text-xs font-mono-ui"
              style={{
                background: bundleActive ? "var(--accent)" : "var(--paper-2)",
                color: bundleActive ? "var(--paper)" : "var(--ink)",
              }}
            >
              {bundleActive
                ? `BUNDLE OF ${count} · ${BUNDLE_PCT}% OFF APPLIED`
                : `ADD ${BUNDLE_MIN - count} MORE · SAVE ${BUNDLE_PCT}% ON THE WHOLE BUNDLE`}
            </div>
          )}
          {items.map((i) => {
            const unit = i.custom_price != null ? i.custom_price : i.price;
            const tip = i.custom_price != null && i.custom_price > i.price ? i.custom_price - i.price : 0;
            return (
              <div key={i.product_id} className="flex gap-3 rule-b pb-4" data-testid={`cart-item-${i.product_id}`}>
                <div className="w-20 h-20 overflow-hidden flex-shrink-0 rounded" style={{ background: "var(--paper-2)" }}>
                  {i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-base truncate">{i.title}</div>
                  <div className="font-mono-ui text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
                    ${unit.toFixed(2)}
                    {tip > 0 && <span className="ml-2" style={{ color: "var(--accent)" }}>· +${tip.toFixed(2)} tip</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="w-7 h-7 rounded-full border" style={{ borderColor: "var(--rule)" }}>−</button>
                    <span className="font-mono-ui text-sm w-6 text-center">{i.quantity}</span>
                    <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="w-7 h-7 rounded-full border" style={{ borderColor: "var(--rule)" }}>+</button>
                    <button onClick={() => remove(i.product_id)} className="ml-auto text-xs underline opacity-60 hover:opacity-100">Remove</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="rule-t px-6 py-5" style={{ background: "var(--paper-2)" }}>
          {items.length > 0 && (
            <>
              {/* Coupon */}
              {!coupon ? (
                <div className="flex gap-2 mb-3">
                  <input
                    data-testid="coupon-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="Promo code"
                    className="flex-1 px-3 py-2 rounded-lg text-sm outline-none font-mono-ui"
                    style={{ background: "var(--paper)", border: "1px solid var(--rule)" }}
                  />
                  <button data-testid="coupon-apply" onClick={applyCode} className="px-3 py-2 rounded-lg text-xs font-mono-ui"
                          style={{ background: "var(--ink)", color: "var(--paper)" }}>
                    APPLY
                  </button>
                </div>
              ) : (
                <div data-testid="coupon-applied" className="mb-3 flex items-center justify-between text-xs font-mono-ui px-3 py-2 rounded-lg"
                     style={{ background: "var(--accent)", color: "var(--paper)" }}>
                  <span>{coupon.code} · {coupon.discount_pct}% OFF</span>
                  <button onClick={() => setCoupon(null)} className="underline">Remove</button>
                </div>
              )}
              {codeErr && <div className="text-xs mb-3" style={{ color: "var(--accent)" }}>{codeErr}</div>}

              <div className="flex justify-between text-sm mb-1">
                <span className="label-mono">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-mono-ui">${total.toFixed(2)}</span>
              </div>
              {bundleDiscount > 0 && (
                <div className="flex justify-between text-sm mb-1" style={{ color: "var(--accent)" }}>
                  <span className="label-mono">Bundle {BUNDLE_PCT}% off</span>
                  <span data-testid="cart-bundle-discount" className="font-mono-ui">−${bundleDiscount.toFixed(2)}</span>
                </div>
              )}
              {couponDiscount > 0 && (
                <div className="flex justify-between text-sm mb-1" style={{ color: "var(--accent)" }}>
                  <span className="label-mono">Coupon {coupon.code}</span>
                  <span data-testid="cart-coupon-discount" className="font-mono-ui">−${couponDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between mb-3 pt-2 rule-t">
                <span className="label-mono">Total</span>
                <span data-testid="cart-total" className="font-mono-ui text-xl">${grandTotal.toFixed(2)}</span>
              </div>
            </>
          )}
          <div className="flex items-center gap-2 text-xs mb-3" style={{ color: "var(--ink-soft)" }}>
            <Lock className="w-3.5 h-3.5" /> Secure checkout by Stripe · Apple Pay · Google Pay
          </div>
          <button
            data-testid="cart-checkout-btn"
            disabled={items.length === 0}
            onClick={() => { setOpen(false); nav("/checkout"); }}
            className="btn-pill w-full justify-center"
          >
            Checkout →
          </button>
        </div>
      </aside>
    </div>
  );
}


// ─── Exit-intent 10% discount modal ───
function ExitIntentModal() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | busy | done | err
  const [coupon, setCoupon] = useState(null);
  const { setCoupon: applyToCart } = useCart();

  useEffect(() => {
    if (localStorage.getItem("meridian_ei_v1")) return;
    let shown = false;
    const onLeave = (e) => {
      if (shown) return;
      if (e.clientY <= 4) {
        shown = true;
        setShow(true);
        localStorage.setItem("meridian_ei_v1", String(Date.now()));
      }
    };
    // Only bind on non-touch devices — exit-intent doesn't exist on mobile
    if (!("ontouchstart" in window)) {
      document.addEventListener("mouseleave", onLeave);
    }
    // Fallback: show after 30s if user hasn't left yet (any device)
    const timer = setTimeout(() => {
      if (!shown && !localStorage.getItem("meridian_ei_v1")) {
        shown = true;
        setShow(true);
        localStorage.setItem("meridian_ei_v1", String(Date.now()));
      }
    }, 30000);
    return () => { document.removeEventListener("mouseleave", onLeave); clearTimeout(timer); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === "busy") return;
    setStatus("busy");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "exit_intent" }),
      });
      const data = await r.json();
      if (data?.coupon) {
        setCoupon(data.coupon);
        applyToCart(data.coupon);
        setStatus("done");
      } else {
        setStatus("err");
      }
    } catch { setStatus("err"); }
  };

  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.55)" }}>
      <div data-testid="exit-intent-modal" className="max-w-md w-full bx p-8 relative" style={{ background: "var(--paper)" }}>
        <button data-testid="exit-intent-close" onClick={() => setShow(false)} className="absolute top-4 right-4 p-1">
          <X className="w-5 h-5" />
        </button>
        <Gift className="w-8 h-8" style={{ color: "var(--accent)" }} />
        <div className="label-mono mt-3" style={{ color: "var(--accent)" }}>/ ONE-TIME OFFER</div>
        <h3 className="font-display text-3xl font-medium tracking-tight mt-2">Before you go —<br />take 10% off.</h3>
        <p className="text-sm mt-3" style={{ color: "var(--ink-soft)" }}>
          Drop your email and we'll send you a first-time code, plus early access to the next drop.
        </p>
        {status !== "done" ? (
          <form onSubmit={submit} className="mt-5 flex gap-2">
            <input
              data-testid="exit-intent-email"
              type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@studio.co"
              className="flex-1 px-4 py-3 rounded-full text-sm outline-none"
              style={{ background: "var(--paper-2)", border: "1px solid var(--rule)" }}
            />
            <button data-testid="exit-intent-submit" disabled={status === "busy"} type="submit"
                    className="px-5 py-3 rounded-full text-sm font-medium"
                    style={{ background: "var(--accent)", color: "var(--paper)" }}>
              {status === "busy" ? "…" : "Send me 10%"}
            </button>
          </form>
        ) : (
          <div data-testid="exit-intent-code" className="mt-5 p-4 rounded-lg text-center"
               style={{ background: "var(--paper-2)" }}>
            <div className="label-mono">YOUR CODE · APPLIED TO BAG</div>
            <div className="wordmark text-3xl mt-1" style={{ color: "var(--accent)" }}>{coupon?.code}</div>
            <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>{coupon?.discount_pct}% off · one-time use</div>
          </div>
        )}
        {status === "err" && <div className="mt-3 text-xs" style={{ color: "var(--accent)" }}>Something went wrong. Try again.</div>}
      </div>
    </div>
  );
}
